<template>
  <div
    ref="videoPlayer"
    class="video-player"
  >
    <transition name="fade">
      <button
        v-if="showOverlay"
        class="video-player__overlay"
        type="button"
        @click="handleOverlayClick"
      >
        <icon-play
          class="video-player__overlay-play"
        />

        <responsive-image
          v-if="showOverlay && image.src"
          class="video-player__image"
          :image="image"
          :lazyload="lazyload"
          min-max="300-1000"
          :padding="false"
          :sizes="sizes"
        />
      </button>
    </transition>

    <div class="video-player__controls">
      <button
        class="video-player__control"
        tabindex="-1"
        type="button"
        @click.prevent="handleControlClick"
      >
        <span
          class="visually-hidden"
          v-text="controlText"
        />

        <component :is="icon" />
      </button>

      <button
        v-if="hasAudioControl"
        class="video-player__control"
        tabindex="-1"
        type="button"
        @click.prevent="toggleAudio"
      >
        <span
          class="visually-hidden"
          v-text="audioText"
        />

        <component :is="audioIcon" />
      </button>

      <button
        v-if="subtitles"
        class="video-player__subtitles"
        :class="{ [$class('active')]: displaySubtitles }"
        type="button"
        @click="displaySubtitles = !displaySubtitles"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.video.subtitles')"
        />

        <span
          aria-hidden="true"
          v-text="$string('accessibility.video.cc')"
        />
      </button>
    </div>

    <video
      ref="video"
      class="video-player__video"
      controlslist="nofullscreen"
      crossorigin="anonymous"
      disablepictureinpicture
      loop
      :muted="isMuted"
      playsinline
      :aria-label="controlText"
      @canplay="state = 'ready'"
      @canplaythrough="state = 'ready'"
      @click="handleControlClick"
      @keyup.enter="handleControlClick"
      @loadedmetadata="state = 'ready'; updateTotalTime()"
      @muted="audio = false"
      @pause="state = 'paused'"
      @play="state = 'playing'"
      @timeupdate="handleVideoTimeUpdate"
    >
      <source
        :src="video.url"
        :type="video.mimeType"
      >

      <track
        v-if="subtitles"
        kind="subtitles"
        label="English"
        :src="subtitles"
        srclang="en"
      >
    </video>

    <div
      v-if="toolbarControls"
      class="video-player__toolbar"
    >
      <div class="video-player__toolbar-inner">
        <div class="video-player__toolbar-controls">
          <button
            class="icon-button"
            tabindex="-1"
            type="button"
            @click="handleControlClick"
          >
            <span
              class="visually-hidden"
              v-text="controlText"
            />

            <component :is="icon" />
          </button>
        </div>

        <div class="video-player__toolbar-times">
          <span
            class="video-player__times-spent text-body-xxs-regular"
            v-text="spentTime"
          />

          <span
            class="video-player__times-divider text-body-xxs-regular"
          />

          <span
            class="video-player__times-spent text-body-xxs-regular"
            v-text="totalTime"
          />
        </div>

        <div class="video-player__toolbar-audio">
          <button
            class="icon-button"
            type="button"
            @click="toggleAudio"
          >
            <span
              class="visually-hidden"
              v-text="audioText"
            />

            <component :is="audioIcon" />
          </button>
        </div>

        <div class="video-player__toolbar-full-screen">
          <button
            class="icon-button"
            type="button"
            @click="handleFullscreenClick"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.video.full_screen')"
            />

            <icon-full-screen />
          </button>
        </div>
      </div>

      <div class="video-player__toolbar-progress">
        <span
          ref="progressPin"
          class="video-player__progress-pin hidden"
        />

        <progress
          ref="progress"
          class="video-player__progress"
          min="0"
          value="0"
          @click="handleClickProgress"
        >
          <span
            ref="progressBar"
            class="video-player__progress-bar"
          />
        </progress>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Video player (video-player)
 * -----------------------------------------------------------------------------
 * Lookbook video player functionality.
 *
 * @param {Boolean} [isActive] - Slide containing video is active.
 * @param {Object} [image] - Shopify image object.
 * @param {String} [subtitles] - Video subtitles URL.
 * @param {String} video - Video URL.
 * @param {Object} [intersectionObserver] - Automatically play video when it
 * scrolls into view, and pause when it leaves view, overrides isActive and
 * autoplay attributes on video load.
 * @param {Boolean} [intersectionObserver.enable] - Enables the observer.
 * @param {String} [intersectionObserver.rootMargin] - Grow or shrink element's
 * bounding box before detecting intersections.
 * @param {Number|Array} [intersectionObserver.threshold] - Sets at what
 * percentage of the element's visibility the callback is executed.
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 *
 */
import { mapGetters } from 'vuex'
import { values as breakpoints } from '~/config/breakpoints'
import { setIntersectionObserver } from '~/helpers/general'

import ResponsiveImage from '~global/images/responsive-image'

import IconPause from '~icons/general/pause.svg'
import IconPlay from '~icons/general/play.svg'
import IconAudio from '~icons/general/audio.svg'
import IconAudioMute from '~icons/general/audio-mute.svg'
import IconFullScreen from '~icons/general/full-screen.svg'

export default {
  name: 'VideoPlayer',

  components: {
    IconPause,
    IconPlay,
    IconAudio,
    IconAudioMute,
    ResponsiveImage,
    IconFullScreen,
  },

  props: {
    image: {
      type: Object,
      default: () => ({
        alt: '',
        height: 0,
        src: '',
        width: 0,
      }),
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    subtitles: {
      type: String,
      default: '',
    },
    video: {
      type: Object,
      default: () => ({
        format: '',
        mimeType: '',
        url: '',
      }),
      required: true,
    },
    toolbarControls: {
      type: Boolean,
      default: false,
    },
    overlayNeeded: {
      type: Boolean,
      default: false,
    },
    hasAudioControl: {
      type: Boolean,
      default: false,
    },
    intersectionObserver: {
      type: Object,
      default: () => ({
        enable: false,
        rootMargin: '0px',
        threshold: 0.1,
      }),
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      displaySubtitles: false,
      overlayPaused: false,
      showOverlay: false,
      isMuted: true,
      audio: false,
      state: 'loading',
      time: 0,
      spentTime: '00:00',
      totalTime: '00:00',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute control text based on state.
     * @returns {String}
     */
    controlText() {
      switch (this.state) {
        case 'loading':
          return this.$string('accessibility.video.loading')

        case 'playing':
          return this.$string('accessibility.video.pause')

        case 'paused':
        default:
          return this.$string('accessibility.video.play')
      }
    },

    /**
     * Compute control text based on state.
     * @returns {String}
     */
    audioText() {
      switch (this.audio) {
        case true:
          return this.$string('accessibility.video.mute')

        case false:
        default:
          return this.$string('accessibility.video.unmute')
      }
    },

    /**
     * Compute icon based on video state.
     * @returns {String}
     */
    icon() {
      switch (this.state) {
        case 'playing':
          return 'icon-pause'

        case 'paused':
        default:
          return 'icon-play'
      }
    },

    /**
     * Compute icon based on video state.
     * @returns {String}
     */
    audioIcon() {
      switch (this.audio) {
        case true:
          return 'icon-audio'

        case false:
        default:
          return 'icon-audio-mute'
      }
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}) 1000px, 300px`
    },
  },

  watch: {

    /**
     * Watch display subtitles to show track.
     */
    displaySubtitles() {
      if (!this.displaySubtitles) {
        this.$refs.video.textTracks[0].mode = 'hidden'
        return
      }

      this.$refs.video.textTracks[0].mode = 'showing'
    },

    /**
     * Watch active overlay to pause/resume (if already playing).
     */
    getActiveOverlay() {
      if (this.getActiveOverlay) {
        if (this.state === 'playing') {
          this.pauseVideo()
          this.overlayPaused = true
          return
        }

        this.overlayPaused = false
        return
      }

      /**
       * When overlay closes if video was previously playing resume it.
       */
      if (this.overlayPaused) {
        this.playVideo()
      }
    },

    /**
     * Watch active state to play video when slide containing it becomes active.
     * - Don't autoplay if prefers reduced motion.
     */
    isActive() {
      this.handleAutoplay()
    },
  },

  mounted() {

    /**
     * Reset any subtitles that browser has enabled by default.
     */
    if (!this.$refs.video.textTracks.length) {
      Object.keys(this.$refs.video.textTracks).forEach((_, index) => {
        this.$refs.video.textTracks[index].mode = 'hidden'
      })
    }

    /**
     * If overlay is needed then show video controls and remove mute.
     */
    if (this.overlayNeeded) {
      this.isMuted = false
      this.audio = true
      this.showOverlay = true
    }

    /**
     * Check isActive state on mounted.
     */
    this.handleAutoplay()
    this.setIntersectionObserver()
    this.setEventListeners()
  },

  methods: {

    /**
     * Handle autoplay.
     */
    handleAutoplay() {
      if (
        'IntersectionObserver' in window &&
        this.intersectionObserver?.enable
      ) {
        return
      }

      if (
        this.isActive &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        this.playVideo()
        return
      }

      this.pauseVideo()
    },

    /**
     * Set intersection observer.
     */
    setIntersectionObserver() {
      if (
        !this.intersectionObserver?.enable ||
        'IntersectionObserver' in window === false
      ) {
        return
      }

      setIntersectionObserver({
        callback: this.handleIntersection,
        element: this.$refs.video,
        rootMargin: this.intersectionObserver.rootMargin || '0px',
        threshold: this.intersectionObserver.threshold || 0.1,
      })
    },

    /**
     * Handle intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          this.pauseVideo()
          return
        }

        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.playVideo()
        }
      })
    },

    /**
     * Handle overlay click.
     */
    handleOverlayClick() {
      if (this.state === 'loading') {
        return
      }

      this.playVideo()
    },

    /**
     * Handle control and video click.
     */
    handleControlClick() {
      if (this.state === 'playing') {
        this.pauseVideo()
        return
      }

      this.playVideo()
    },

    /**
     * Play video.
     */
    async playVideo() {
      try {
        await this.$refs.video.play()
        this.showOverlay = false

        if (this.toolbarControls) {
          this.$refs.progressPin.classList.remove('hidden')
        }

      } catch (error) {
        cnvs.ReportError('Failed to play video', error)
      }
    },

    /**
     * Pause video.
     */
    async pauseVideo() {
      try {
        await this.$refs.video.pause()

      } catch (error) {
        cnvs.ReportError('Failed to pause video', error)
      }
    },

    /**
     * Handle time update on video.
     */
    handleVideoTimeUpdate() {
      this.time = this.$refs.video?.currentTime || 0
      this.state = 'playing'

      if (this.toolbarControls) {
        this.totalTime = this.timeParse(this.$refs.video?.duration)
        this.spentTime = this.timeParse(this.$refs.video?.currentTime)
        this.handleProgressBar()
        this.$refs.progressPin.style.left = `${Math.floor((this.$refs.video.currentTime / this.$refs.video.duration) * 100)}%`
      }
    },

    handleProgressBar() {
      if (!this.$refs.progress.getAttribute('max')) {
        this.$refs.progress.setAttribute('max', this.$refs.video.duration)
      }

      this.$refs.progress.value = this.$refs.video.currentTime
      this.$refs.progressBar.style.width = `${Math.floor((this.$refs.video.currentTime / this.$refs.video.duration) * 100)}%`
    },

    handleClickProgress() {
      const pos = event.offsetX / this.$refs.progress.offsetWidth
      this.$refs.video.currentTime = pos * this.$refs.video.duration
    },

    updateTotalTime() {
      if (this.toolbarControls) {
        this.totalTime = this.timeParse(this.$refs.video?.duration)
      }
    },

    /**
     * Handle audio toggle.
     */
    toggleAudio() {
      if (this.audio) {
        this.$refs.video.muted = 'muted'
        this.audio = false
      } else {
        this.$refs.video.muted = false
        this.audio = true
      }
    },

    /**
     * Format epoch time into human-readable date
     * @param {Number} sec - epoch time
     * @returns {String} - Formated time string
     */
    timeParse(sec) {
      let min = 0
      min = Math.floor(sec / 60)
      // eslint-disable-next-line
      sec -= min * 60

      return `${this.convertTwoDigit(min)}:${this.convertTwoDigit(sec)}`
    },

    /*
     * Convert number value into two digits before 10
     * @param {Number} - day / month
     * @returns {String} - converted value
     */
    convertTwoDigit(value) {
      return `0${Math.floor(value)}`.slice(-2)
    },

    /**
     * Handle fullscreen click.
     */
    handleFullscreenClick() {
      this.enterFullscreen()
    },

    /**
     * Enter fullscreen.
     */
    enterFullscreen() {
      const video = this.$refs.video

      if (video.requestFullscreen) {
        video.requestFullscreen()
      } else if (video.mozRequestFullScreen) {
        video.mozRequestFullScreen()
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen()
      } else if (video.webkitRequestFullScreen) {
        video.webkitRequestFullScreen()
      } else if (video.msRequestFullscreen) {
        video.msRequestFullscreen()
      } else if (video.webkitSupportsFullscreen) {
        video.webkitEnterFullscreen()
      }
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      if (this.toolbarControls) {

        /**
         * Detect when user on fullscreen.
         */
        const prefixes = [
          '',
          'moz',
          'webkit',
          'ms',
          'onwebkit',
          'webkitIs',
        ]

        prefixes.forEach((prefix) => {
          document.addEventListener(`${prefix}fullscreenchange`, () => {
            if (document.fullscreenElement ||
              document.mozFullScreenElement ||
              document.webkitFullscreenElement ||
              document.msFullscreenElement
            ) {
              this.fullscreen = true
              this.$refs.videoPlayer.classList.add('is-fullscreen')
              this.$refs.video.setAttribute('controls', true)

            } else {
              this.fullscreen = false
              this.$refs.videoPlayer.classList.remove('is-fullscreen')
              this.$refs.video.removeAttribute('controls')
              this.exitFullscreen()
            }
          })
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import './video-player';
</style>
